<script setup lang="ts">
    import { CardMenu } from '@/components/ui/card';
    import { columns } from '@/tables/columns/work-calendars';
    import { Head } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import CreateWorkCalendar from '@/components/work-calendars/CreateWorkCalendar.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import type { BreadcrumbItem, NavItem } from '@/types';

    const { hasPermission } = usePermissions();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'dashboards.payroll',
        },
        {
            title: 'menu.salary_setup',
        },
    ];

    const navItems: NavItem[] = [
        {
            title: 'holidays.page_header',
            href: route('v2.salary.holidays.index'),
            permissionLevel: Permission.HOLIDAYS_VIEW,
        },
        {
            title: 'work-calendars.page_header',
            href: route('v2.salary.work-calendars.index'),
            permissionLevel: Permission.WORK_CALENDARS_VIEW,
        },
    ];

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.WORK_CALENDARS],
        queryFn: getWorkCalendars,
    });

    async function getWorkCalendars() {
        const { data } = await axios.get(route('v2.salary.work-calendars.table'));
        return data.data;
    }
</script>

<template>
    <Head :title="$t('work-calendars.page_header')" />

    <AppLayout
        :breadcrumbs="breadcrumbs"
        header-class="w-full"
    >
        <PageCard>
            <DataTable
                :table-key="QueryKey.WORK_CALENDARS"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :default-sorting="[
                    {
                        id: 'name',
                        desc: false,
                    },
                ]"
            >
                <template #title>
                    <CardMenu :items="navItems" />
                </template>
                <template
                    #actions
                    v-if="hasPermission(Permission.WORK_CALENDARS_CREATE)"
                >
                    <DataTableToolbarActions name="work-calendars">
                        <CreateWorkCalendar
                            v-if="hasPermission(Permission.WORK_CALENDARS_CREATE)"
                        />
                    </DataTableToolbarActions>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
